import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Volume2, X } from '../icons';
import { supabase } from '../../lib/supabase';
import { Conversation } from '@elevenlabs/client';
import { formatCharacteristicScores, formatHarmonicScores, getDominantHarmonic } from '../../lib/assessment-context';
import type { Score } from '../../types';
import type { HarmonicScore } from '../../types/harmonic';

interface AICoachVoiceProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  respondentName: string;
  scores: Score[];
  harmonicScores: HarmonicScore[];
}

interface BriefVariables {
  respondent_name: string;
  assessment_results: string;
  harmonic_results: string;
  dominant_harmonic: string;
  top_characteristics: string;
  ai_analysis: string;
  pathway_context: string;
  coach_notes: string;
  respondent_brief: string;
}

async function fetchBriefVariables(): Promise<BriefVariables | null> {
  try {
    const { data, error } = await supabase.functions.invoke<BriefVariables>('ai-coach-brief', {
      body: {},
    });
    if (error || !data) return null;
    return data;
  } catch {
    return null;
  }
}

export function AICoachVoice({ isOpen, onClose, agentId, respondentName, scores, harmonicScores }: AICoachVoiceProps) {
  const [status, setStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [duration, setDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const conversationRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (conversationRef.current) {
        conversationRef.current.endSession().catch(() => {});
      }
    };
  }, []);

  const startCall = useCallback(async () => {
    setStatus('connecting');
    setErrorMsg('');
    setDuration(0);

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const briefVars = await fetchBriefVariables();
      const dynamicVariables: Record<string, string> = briefVars
        ? {
            respondent_name: briefVars.respondent_name || respondentName || 'Unknown',
            assessment_results: briefVars.assessment_results || formatCharacteristicScores(scores),
            harmonic_results: briefVars.harmonic_results || formatHarmonicScores(harmonicScores),
            dominant_harmonic: briefVars.dominant_harmonic || getDominantHarmonic(harmonicScores),
            top_characteristics: briefVars.top_characteristics,
            ai_analysis: briefVars.ai_analysis,
            pathway_context: briefVars.pathway_context,
            coach_notes: briefVars.coach_notes,
            respondent_brief: briefVars.respondent_brief,
          }
        : {
            respondent_name: respondentName || 'Unknown',
            assessment_results: formatCharacteristicScores(scores),
            harmonic_results: formatHarmonicScores(harmonicScores),
            dominant_harmonic: getDominantHarmonic(harmonicScores),
            top_characteristics: '',
            ai_analysis: '',
            pathway_context: '',
            coach_notes: '',
            respondent_brief: '',
          };

      const conversation = await Conversation.startSession({
        agentId,
        connectionType: 'websocket',
        dynamicVariables,
        onConnect: () => {
          setStatus('connected');
          startTimeRef.current = Date.now();
          timerRef.current = setInterval(() => {
            setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
          }, 1000);
        },
        onDisconnect: () => {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          const callDuration = startTimeRef.current
            ? Math.floor((Date.now() - startTimeRef.current) / 1000)
            : 0;
          setStatus('idle');
          setDuration(0);
          conversationRef.current = null;

          if (callDuration > 0) {
            supabase.functions.invoke('ai-coach-voice-log', {
              body: { durationSeconds: callDuration },
            }).catch(() => {});
          }
        },
        onError: (error: any) => {
          console.error('Voice session error:', error);
          setErrorMsg(typeof error === 'string' ? error : 'Voice connection error');
          setStatus('error');
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          conversationRef.current = null;
        },
      });

      conversationRef.current = conversation;
    } catch (err: any) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow microphone access and try again.'
        : err?.message || 'Failed to start voice session';
      setErrorMsg(msg);
      setStatus('error');
    }
  }, [agentId, respondentName, scores, harmonicScores]);

  const endCall = useCallback(async () => {
    if (conversationRef.current) {
      try {
        await conversationRef.current.endSession();
      } catch {
        // ignore
      }
      conversationRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const callDuration = startTimeRef.current
      ? Math.floor((Date.now() - startTimeRef.current) / 1000)
      : 0;
    setStatus('idle');
    setDuration(0);

    if (callDuration > 0) {
      supabase.functions.invoke('ai-coach-voice-log', {
        body: { durationSeconds: callDuration },
      }).catch(() => {});
    }
  }, []);

  const handleClose = useCallback(() => {
    if (status === 'connected') {
      endCall();
    }
    onClose();
  }, [status, endCall, onClose]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-4 right-4 w-[380px] bg-white rounded-2xl shadow-2xl border border-cream-200 flex flex-col z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-warm-500 to-warm-600 text-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Volume2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">AI Coach</h3>
                <p className="text-xs text-warm-100">Voice Call</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 flex flex-col items-center">
            {status === 'idle' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
              >
                <div className="w-20 h-20 bg-warm-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Volume2 className="w-10 h-10 text-warm-500" />
                </div>
                <p className="text-sm text-chocolate-600 font-medium mb-2">Voice AI Coach</p>
                <p className="text-xs text-chocolate-400 mb-6 max-w-[260px]">
                  Start a voice conversation with your AI coaching assistant.
                </p>
                <button
                  onClick={startCall}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-colors font-medium text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Start Call
                </button>
              </motion.div>
            )}

            {status === 'connecting' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-4"
              >
                <div className="w-20 h-20 bg-warm-100 rounded-full flex items-center justify-center mx-auto mb-5 animate-pulse">
                  <Phone className="w-10 h-10 text-warm-500" />
                </div>
                <p className="text-sm text-chocolate-600 font-medium">Connecting...</p>
                <p className="text-xs text-chocolate-400 mt-1">Setting up your voice session</p>
              </motion.div>
            )}

            {status === 'connected' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center w-full"
              >
                <div className="relative w-20 h-20 mx-auto mb-5">
                  <div className="absolute inset-0 bg-sage-200 rounded-full animate-ping opacity-30" />
                  <div className="relative w-20 h-20 bg-sage-500 rounded-full flex items-center justify-center">
                    <Volume2 className="w-10 h-10 text-white" />
                  </div>
                </div>

                <p className="text-2xl font-mono font-semibold text-chocolate-800 mb-1">
                  {formatTime(duration)}
                </p>
                <p className="text-xs text-sage-600 font-medium mb-4">Call in progress</p>

                <button
                  onClick={endCall}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-warm-600 text-white rounded-xl hover:bg-warm-700 transition-colors font-medium text-sm"
                >
                  <PhoneOff className="w-4 h-4" />
                  End Call
                </button>
              </motion.div>
            )}

            {status === 'error' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-4"
              >
                <p className="text-sm text-warm-600 mb-3">{errorMsg || 'Connection failed'}</p>
                <button
                  onClick={startCall}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-sage-600 text-white rounded-xl hover:bg-sage-700 transition-colors font-medium text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Try Again
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
